import React from "react";
import {
  DepressionContentPage,
  FAQItem,
} from "@/components/DepressionContentPage";
import { RelatedLink } from "@/components/RelatedPages";
import { buildMedicalClinicLocal, Crumb } from "@/components/schemaConstants";
import CrisisLifelineBanner from "@/components/CrisisLifelineBanner";

/**
 * /young-adult-depression-treatment
 *
 * Depression care for adults 18–25. SAINT® is for adults 22+ only; the page
 * states this explicitly and never implies SAINT for anyone under 22. No
 * guarantees; eligibility decided at consultation. Ends with 988 crisis line.
 */
const faqs: FAQItem[] = [
  {
    question: "Can I get treatment without my parents knowing?",
    answer:
      "Because you are an adult (18+), your care is confidential. We will not share your clinical details with anyone, including parents, without your written consent. You can involve your family as much or as little as you choose.",
  },
  {
    question: "Can my parent book for me?",
    answer:
      "Yes. A parent can call us with you to help schedule and ask questions. Because you are over 18, we will need your consent to share any clinical details, and we will walk you both through it.",
  },
  {
    question: "Does insurance cover TMS for young adults?",
    answer:
      "TMS for depression may be covered when your plan's criteria are met. Many young adults stay on a parent's plan until age 26. We verify your benefits for free before you start, whatever carrier you have.",
  },
  {
    question: "Is TMS safe at my age?",
    answer:
      "TMS is a non-invasive, outpatient, medication-free treatment with no anesthesia. Individual results vary, and your physician decides whether TMS is appropriate for you after an evaluation.",
  },
  {
    question: "What if antidepressants haven't worked?",
    answer:
      "If you have tried antidepressants without enough relief, there are still options — TMS, SPRAVATO® for treatment-resistant depression, therapy, and medication adjustments. SAINT® accelerated TMS is available for adults 22 and older. We help you sort through what may fit your situation.",
  },
  {
    question: "Can I keep going to class or work during treatment?",
    answer:
      "Yes. Standard TMS sessions are short (about 20 minutes) with no downtime, and we offer early and late appointment times. SAINT® is a 5-day accelerated protocol for adults 22+. We plan treatment around your schedule whenever possible.",
  },
];

const relatedLinks: RelatedLink[] = [
  {
    title: "College Student Depression Treatment",
    href: "/college-student-depression-treatment",
    description: "Depression care built around an academic schedule.",
  },
  {
    title: "Diagnosed With Depression as a Young Adult",
    href: "/diagnosed-with-depression-as-a-young-adult",
    description: "What a diagnosis in your 20s means and what to do next.",
  },
  {
    title: "Depression in College",
    href: "/depression-in-college",
    description: "Recognizing depression when you're away from home.",
  },
  {
    title: "TMS Therapy",
    href: "/services/tms",
    description: "Medication-free magnetic stimulation for depression.",
  },
];

const breadcrumbs: Crumb[] = [
  { name: "Home", href: "/" },
  { name: "Depression Treatment", href: "/depression-treatment-coral-springs" },
  { name: "Young Adults (18–25)", href: "/young-adult-depression-treatment" },
];

const YoungAdultDepressionTreatment: React.FC = () => {
  return (
    <>
      <DepressionContentPage
        seo={{
          title:
            "Depression Treatment for Young Adults (18–25) in Coral Springs, FL | Neuro Wellness",
          description:
            "Outpatient depression treatment for young adults 18–25: therapy, medication management, TMS and SPRAVATO®. Scheduling around school and work. Free consultation.",
          canonical: "/young-adult-depression-treatment",
          keywords:
            "young adult depression treatment Coral Springs, depression treatment 18-25 Florida, college student depression, TMS young adults, depression help 20s",
          breadcrumbs,
          extraSchema: [buildMedicalClinicLocal()],
        }}
        badge="Coral Springs, FL · Ages 18–25"
        heroTitle="Depression Treatment for Young Adults in Coral Springs, FL"
        heroSubtitle="Your 20s are supposed to be the start of everything. Depression doesn't have to put that on hold."
        heroCtaPrimary="Book a Free Consultation"
        heroCtaPrimaryHref="/#consultation"
        reviewedBy="Daniel Pietras, MD"
        lastUpdated="2026-11-04"
        sections={[
          {
            heading: "What young-adult depression often looks like",
            variant: "text",
            body: (
              <>
                <p>
                  Depression in your late teens and twenties doesn&rsquo;t
                  always look like sadness. It can look like pulling away from
                  friends, dropping classes or calling out of shifts, sleeping
                  too much or too little, and a nagging sense that you are stuck
                  or falling behind your peers.
                </p>
                <p>
                  You might feel numb more than sad, or find that things you
                  used to enjoy — hobbies, social plans, goals — just
                  don&rsquo;t pull you anymore. These are real symptoms of a
                  treatable condition, not a personal failure or a sign you
                  &ldquo;aren&rsquo;t trying hard enough.&rdquo;
                </p>
              </>
            ),
          },
          {
            heading: "Treatment options for adults 18+",
            variant: "cards",
            cards: [
              {
                title: "Individual & Group Therapy",
                body: "Talk therapy for the concerns young adults bring — identity, relationships, school and career pressure, and leaving home.",
              },
              {
                title: "Medication Management",
                body: "A careful review of any current medications and whether an antidepressant is the right fit, with honest talk about side effects.",
              },
              {
                title: "TMS Therapy",
                body: "Medication-free magnetic stimulation, about 20 minutes a session, no downtime. Individual results vary; eligibility decided at consultation.",
              },
              {
                title: "SPRAVATO® (Esketamine)",
                body: "FDA-approved nasal spray for adults with treatment-resistant depression, given in-office with monitoring.",
              },
            ],
          },
          {
            heading: "A note on accelerated TMS",
            variant: "callout",
            callout:
              "SAINT® is an accelerated, fMRI-guided 5-day TMS protocol available for adults 22 and older. It is not an option for anyone under 22. Standard TMS, therapy, medication management, and SPRAVATO® are available to adults 18+.",
          },
          {
            heading: "Built around school and work",
            variant: "text",
            body: (
              <>
                <p>
                  We offer early and late appointment times so treatment can fit
                  around classes, shifts, and internships. Care can be planned
                  around semester breaks, and standard TMS sessions are short —
                  about 20 minutes with no downtime — so you can come in and get
                  back to your day.
                </p>
                <p>
                  If you are juggling school deadlines or a new job, we will
                  talk through a schedule that is realistic for you rather than
                  adding another source of stress.
                </p>
              </>
            ),
          },
          {
            heading: "For parents",
            variant: "text",
            body: (
              <>
                <p>
                  If your adult child is struggling, you can call us with them.
                  Because they are over 18, we will need their consent to share
                  details, and we will walk you both through it. Many young
                  adults do better when a parent helps with the first call and
                  the first visit, then steps back as they feel ready.
                </p>
                <p>
                  See our guides for families:{" "}
                  <a
                    href="/adult-child-with-depression"
                    className="text-accent font-semibold underline underline-offset-4"
                  >
                    adult child with depression
                  </a>{" "}
                  and{" "}
                  <a
                    href="/help-your-adult-child-through-depression-without-taking-over"
                    className="text-accent font-semibold underline underline-offset-4"
                  >
                    helping your adult child without taking over
                  </a>
                  .
                </p>
              </>
            ),
          },
          {
            heading: "Insurance for young adults",
            variant: "text",
            body: (
              <>
                <p>
                  You can stay on a parent&rsquo;s health plan until age 26,
                  which often makes coverage easier during these years. We work
                  with student plans such as Wellfleet — see{" "}
                  <a
                    href="/wellfleet-student-plan-insurance"
                    className="text-accent font-semibold underline underline-offset-4"
                  >
                    Wellfleet student plan insurance
                  </a>{" "}
                  — and we verify your benefits for free, whatever carrier you
                  have.
                </p>
              </>
            ),
          },
        ]}
        faq={faqs}
        faqHeading="Frequently Asked Questions"
        relatedLinks={relatedLinks}
        relatedHeading="Related Resources"
        finalCtaHeading="Your 20s shouldn't be on hold"
        finalCtaText="Call our Coral Springs team at 954-903-1323 or book a free consultation. We'll help you understand your options and verify your insurance at no cost. Eligibility is decided at the consultation; individual results vary."
        finalCtaPrimaryLabel="Book a Free Consultation"
        finalCtaPrimaryHref="/#consultation"
        finalCtaSecondaryLabel="Call 954-903-1323"
        finalCtaSecondaryHref="tel:9549031323"
      />
      <div className="container-px mx-auto max-w-3xl px-4 pb-12">
        <CrisisLifelineBanner />
      </div>
    </>
  );
};

export default YoungAdultDepressionTreatment;
